import React, { useState } from "react";
import { CardProps, Radio } from "@mui/material";
import AddShoppingCartOutlined from "@mui/icons-material/AddShoppingCartOutlined";
import { useSearchParams } from "react-router-dom";

// Utils & Enum
import { getTranslateByKey, isEmpty, isNullUndefined, isShowControlByElementId } from "@/main/services/utils";
import { formatCurrency } from "@/main/services/utilsNumber";
import { PartTypeEnum } from "@/main/services/enum/PartTypeEnum";
import { IMAGE_DEFAULT } from "@/main/services/constants";
import { useCreateOrderContext } from "@/main/screens/order/createorder/context";

// Models
import { QuoteSelectBestSupplierRequest } from "@/main/models/request/quoteRequest";

// Api
import { quoteApi } from "@/main/api";

// Styles
import * as S from "./styles";

// Redux
import { useUserReducer } from "@/main/store/user";
import { CartPart, useCartReducer } from "@/main/store/cart";

// Components
import FavoriteButton from "../favoriteButton/favoriteButton";
import NotQuotedOrAwaitingAnswerInfo from "./notQuotedOrAwaitingAnswerInfo";
import CountControl from "../countControl/countControl";
import { ColumnCartLayout } from "./columnCartLayout/columnCartLayout";
import Card from "./card";
import PriceDiffPercent from "../priceDiffPercent/priceDiffPercent";
import { Controls } from "@/main/services/constants/constantsControl";
import StarRatingPartially from "../starRating/starRatingPartially";
import { useVisualIdentityReducer } from "@/main/store/visualIdentity";

export interface ISupplierCard {
	id: string;
	guid: string;
	name: string;
	isRecommended?: boolean;
}
export interface IPartsCard {
	id: string;
	guid: string;
	guidPart: string;
	image?: string;
	title: string;
	subTitle: string;
	description: string;
	partNumber: string;
	partType: string;
	price: number;
	idPartType: number;
	shippingCost: number;
	deliveryTime: string;
	rating?: number;
	stock: number;
	category: string;
	subCategory: string;
	guidSupplierInventoryWarehouse: string;
	guidQuoteSupplierItem: string;
	partBrandDescription: string;
	partBrandGuid: string;
	isRecomendedSupplier?: boolean;
	isFavoriteSupplier?: boolean;
	notQuoted?: boolean;
	externalPartId: string;
	deliveryBySupplier?: boolean;
	reason?: string;
	reasonDescription?: string;
	priceDiffPercent?: number;
	shipingIntegrationIsUnavailable?: boolean;
}

export interface IPartInCart extends CartPart {
	hasMoreSuppliers?: boolean;
	supplier: ISupplierCard;
}
interface ICardShoppingProps extends CardProps {
	isBlocked?: boolean;
	isShowSupplier?: boolean;
	optionSelectedPart?: string;
	supplier?: ISupplierCard;
	part: IPartsCard;
	id?: string;
	partInCart?: IPartInCart;
	onClick?: (event: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
	onClickCart?: (part: IPartsCard, supplier: ISupplierCard, quantityUpdate?: number) => void;
	displayCart?: () => void;
	onSelectedPart?: (value: string, checked: boolean) => void;
}

function CardShopping({
	supplier,
	part,
	partInCart = undefined,
	isShowSupplier = false,
	optionSelectedPart = undefined,
	onClickCart = () => {},
	displayCart = () => {},
	onSelectedPart = () => {},
	isBlocked = false,
	...props
}: ICardShoppingProps) {
	const userReducer = useUserReducer();
	const cartReducer = useCartReducer();
	const { visualIdentity: visualIdentityReducer } = useVisualIdentityReducer();
	const [searchParams] = useSearchParams();
	const guidQuote = searchParams.get("guidQuote");
	const [isLoadingAddToCart, setIsLoadingAddToCart] = useState<boolean>(false);

	const { integration, isQuoteOpen, isQuoteManual } = useCreateOrderContext();

	const showPrices = isShowControlByElementId(Controls.IdShowPrices);
	const showButtonAddCart = isQuoteManual ? isShowControlByElementId(Controls.IdAddPartToCart) : true;

	const generateCardId = (name: string) => {
		if (!name) return "id-card-default";
		return `id-card-${name.replace(/[^a-z0-9]/gi, "-").toLowerCase()}`;
	};

	const cardId = generateCardId(supplier?.name || "default-supplier");

	function handleClickCart(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.stopPropagation();

		if (supplier) {
			onClickCart(part, supplier);
		}
	}

	function handleSelectPart(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) {
		e.preventDefault();
		e.stopPropagation();
		onSelectedPart(e.target.value, checked);
	}

	async function handleClickAddCartSupplierRecommend(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
		e.stopPropagation();
		setIsLoadingAddToCart(true);
		const request = {
			quoteGuid: guidQuote,
			partGuid: part.guid,
			findMoreSuppliers: false,
			automaticQuote: false,
		} as QuoteSelectBestSupplierRequest;

		await quoteApi
			.getSelectBestSupplier(request)
			.then((res) => {
				const resData = res.data.result;
				const supplierRequest: ISupplierCard = {
					guid: resData.supplierGuid,
					id: resData.supplierGuid,
					isRecommended: true,
					name: resData.supplier,
				};
				const partRequest: IPartsCard = {
					...part,
					deliveryTime: String(resData.deliveryDays),
					guidSupplierInventoryWarehouse: resData.supplierInventory_WarehouseGuid,
					idPartType: resData.idPartType,
					price: resData.price,
					stock: resData.stock,
					guid: resData.quoteItemGuid,
					image: IMAGE_DEFAULT,
					shippingCost: resData.shippingCost,
					guidQuoteSupplierItem: resData.guidQuoteSupplierItem,
				};
				onClickCart(partRequest, supplierRequest);
			})
			.catch(() => {})
			.finally(() => setIsLoadingAddToCart(false));
	}

	function renderPartTypeAndPartBrand(part: IPartsCard) {
		if (!part.idPartType || !part.partType) {
			return " ";
		}
		if (part.idPartType !== PartTypeEnum.OEM) {
			const partBrandText = part.partBrandDescription ? `(${part.partBrandDescription})` : "";
			return ` ${isEmpty(part.partNumber) ? "" : "-"} ${part.partType} ${partBrandText}`;
		}

		return ` ${isEmpty(part.partNumber) ? "" : "-"} ${part.partType}`;
	}

	async function callIncrementCart(partInCartSelected: IPartInCart, count: number) {
		if (partInCartSelected.hasMoreSuppliers) {
			displayCart();
			throw new Error(getTranslateByKey("information.moreThanOneSupplier", true));
		}
		const { supplier, ...restPartInCartSelected } = partInCartSelected;

		onClickCart(restPartInCartSelected as any, supplier, count);
	}
	async function callDecrementCart(partInCartSelected: IPartInCart, count: number) {
		if (partInCartSelected.hasMoreSuppliers) {
			displayCart();
			throw new Error(getTranslateByKey("information.moreThanOneSupplier", true));
		}
		const { supplier, ...restPartInCartSelected } = partInCartSelected;
		onClickCart(restPartInCartSelected as any, supplier, count);
	}

	const MAX_COUNT_MORE_SUPPLIER = 999;
	const uniqueIdPart = String(part.guidQuoteSupplierItem).concat(part.guidSupplierInventoryWarehouse);

	return (
		<Card
			className={`${
				(!userReducer.userPreference.preferences.isViewCard && "box-layout-card-collumn") || ""
			} ${supplier?.isRecommended ? "supplier-recommended" : ""}`}
			maxWidth={userReducer.userPreference.preferences.isViewCard ? "400px" : "100%"}
			borderColor={supplier?.isRecommended ? visualIdentityReducer.colors.pantone2726C : null}
			{...props}
			isBlocked={isBlocked}
		>
			<S.ContainerCardShopping
				id={cardId}
				active={userReducer.userPreference.preferences.isViewCard}
				$isShowSupplier={isShowSupplier}
			>
				{isShowSupplier && (
					<S.HeaderCardShopping>
						<S.TitleHeaderCardShopping title={supplier?.name}>{supplier?.name}</S.TitleHeaderCardShopping>
						<FavoriteButton
							notClickable={isBlocked}
							isFavorite={part.isFavoriteSupplier ? part.isFavoriteSupplier : false}
							onToggleFavorite={() => {}}
						/>
					</S.HeaderCardShopping>
				)}
				<S.BodyCardShopping>
					{part.image && <img src={part.image} alt="" />}
					{isShowSupplier ? (
						!isBlocked && (
							<>
								<S.AreaTitleCardShoppingSupplier title={part.partNumber + renderPartTypeAndPartBrand(part)}>
									<S.TitleCardShoppingSupplier>{part.partNumber}</S.TitleCardShoppingSupplier>
									{renderPartTypeAndPartBrand(part)}
								</S.AreaTitleCardShoppingSupplier>
								<S.TextCardShopping title={part.title}>{part.title}</S.TextCardShopping>
							</>
						)
					) : (
						<>
							<S.AreaTitleCardShopping>
								<S.TitleCardShopping title={part.title}>{part.title}</S.TitleCardShopping>
							</S.AreaTitleCardShopping>
							<S.TextCardShopping title={`${part.category} > ${part.subCategory}`}>
								{`${part.category} > ${part.subCategory}`}
							</S.TextCardShopping>
						</>
					)}
					{isShowSupplier && !isBlocked && (
						<div>
							<S.TextCardShopping title={part.deliveryTime}>{part.deliveryTime}</S.TextCardShopping>
						</div>
					)}
				</S.BodyCardShopping>

				{isShowSupplier && !isBlocked && (
					<S.FooterCardShopping>
						<StarRatingPartially rating={!isNullUndefined(part.rating) ? part.rating : 0} />
						<S.PriceContainer $hasMore2Items={showPrices}>
							<S.PriceCardShopping>
								{showPrices && part.price !== undefined && formatCurrency(part.price)}
								{!isNullUndefined(part.priceDiffPercent) && (
									<PriceDiffPercent diffPercent={Number(part.priceDiffPercent)} />
								)}
							</S.PriceCardShopping>
							{optionSelectedPart !== undefined ? (
								<Radio
									checked={optionSelectedPart === uniqueIdPart}
									onChange={handleSelectPart}
									color="primary"
									value={uniqueIdPart}
									name="radio-button-selected-part"
								/>
							) : partInCart ? (
								<CountControl
									isBlocked={(integration && isQuoteOpen) || !showButtonAddCart || isBlocked}
									initialCount={partInCart.quantity}
									maxCount={partInCart.stock}
									onDecrement={(count) => callDecrementCart(partInCart, count)}
									onIncrement={(count) => callIncrementCart(partInCart, count)}
								/>
							) : (
								showButtonAddCart && (
									<S.IconButtonCart
										onClick={handleClickCart}
										id={"id-createOrder-button-add-cart"}
										disabled={
											(integration && isQuoteOpen) ||
											!showButtonAddCart ||
											cartReducer.cartItems.isLoading ||
											cartReducer.cartItems.isLoadingUpdate
										}
									>
										<AddShoppingCartOutlined fontSize="medium" />
									</S.IconButtonCart>
								)
							)}
						</S.PriceContainer>
					</S.FooterCardShopping>
				)}

				{!isShowSupplier &&
					(partInCart ? (
						<CountControl
							isBlocked={(integration && isQuoteOpen) || !showButtonAddCart || isBlocked}
							initialCount={partInCart.quantity}
							maxCount={partInCart.hasMoreSuppliers ? MAX_COUNT_MORE_SUPPLIER : partInCart.stock}
							onDecrement={(count) => callDecrementCart(partInCart, count)}
							onIncrement={(count) => callIncrementCart(partInCart, count)}
						/>
					) : (
						showButtonAddCart && (
							<S.BoxButtonCart
								onClick={(e) => handleClickAddCartSupplierRecommend(e)}
								className="ignore-click"
								disabled={(integration && isQuoteOpen) || !showButtonAddCart || isLoadingAddToCart}
							>
								<AddShoppingCartOutlined fontSize="medium" />
							</S.BoxButtonCart>
						)
					))}

				{part.shipingIntegrationIsUnavailable && part.shipingIntegrationIsUnavailable && (
					<S.AwaitingAnswer>
						<div className="d-flex flex-column">
							{getTranslateByKey("information.unavailableErrorConnectionToShippingCompany")}
						</div>
					</S.AwaitingAnswer>
				)}

				{isBlocked && !(part.shipingIntegrationIsUnavailable && part.shipingIntegrationIsUnavailable) && (
					<S.AwaitingAnswer>
						<NotQuotedOrAwaitingAnswerInfo
							reasonDescription={part.reasonDescription}
							reason={part.reason}
							notQuoted={part.notQuoted}
							isQuoteManual={isQuoteManual}
							isQuoteOpen={isQuoteOpen}
						/>
					</S.AwaitingAnswer>
				)}
			</S.ContainerCardShopping>
			<ColumnCartLayout
				uniqueIdPart={uniqueIdPart}
				optionSelectedPart={optionSelectedPart}
				part={part}
				supplier={supplier}
				isFavorite={part.isFavoriteSupplier ? part.isFavoriteSupplier : false}
				isShowSupplier={isShowSupplier}
				isBlocked={isBlocked}
				isLoadingAddToCart={isLoadingAddToCart}
				integration={integration}
				isQuoteOpen={isQuoteOpen}
				isQuoteManual={isQuoteManual}
				partInCart={partInCart}
				notQuoted={part.notQuoted}
				handleClickCartSupplier={handleClickCart}
				handleToggleFavorite={() => {}}
				handleClickAddCartSupplierRecommend={handleClickAddCartSupplierRecommend}
				callDecrementCart={callDecrementCart}
				callIncrementCart={callIncrementCart}
				handleSelectPart={handleSelectPart}
			/>
		</Card>
	);
}

export default CardShopping;
